import { Fragment, useState, useEffect } from 'react';
import { Download, DollarSign, User, Calendar, Building2, CheckCircle, Clock, FileSpreadsheet, Loader2, FileText, Truck, X } from 'lucide-react';
import { expenseService } from '../services/expenseService';
import { userService } from '../services/userService';
import { expenseCategoryService } from '../services/expenseCategoryService';

interface Expense {
  id: string;
  expenseNo?: string;
  userId: string;
  userName: string;
  category: string;
  amount: number;
  date: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedAt?: string;
  receiptUrl?: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  iban?: string;
  ocrData?: {
    merchant?: string;
    total?: number;
    date?: string;
    taxNumber?: string;
    rawText?: string;
    suggestedCategory?: string;
    summary?: {
      subtotal?: number | null;
      vat?: number | null;
      grandTotal?: number | null;
    };
    items?: Array<{ description: string; amount: number; raw: string }>;
  };
}

interface Personnel {
  id: string;
  name: string;
  iban?: string;
  bankName?: string;
  role: string;
  department: string;
}

interface PaymentOrder {
  id: string;
  personnelId: string;
  personnelName: string;
  iban: string;
  bankName: string;
  totalAmount: number;
  expenseIds: string[];
  expenseCount: number;
  status: 'pending' | 'sent' | 'paid';
  createdAt: string;
  sentAt?: string;
  paidAt?: string;
}

export function PaymentOrders() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [paymentOrders, setPaymentOrders] = useState<PaymentOrder[]>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<string[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('all');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState<string | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const resolveFileUrl = (filePath?: string | null) => {
    if (!filePath) return '#';
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
    return `${apiBase}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
  };

  const parseOcrMetadata = (value: any) => {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [expensesRes, usersRes, ordersRes, categoriesRes] = await Promise.all([
        expenseService.getExpenses({ status: 'approved' }),
        userService.getUsers(),
        expenseService.getPaymentOrders(),
        expenseCategoryService.getCategories(),
      ]);

      if (expensesRes.success && expensesRes.data) {
        setExpenses(expensesRes.data.map((e: any) => ({
          id: String(e.id),
          expenseNo: e.expense_no || '',
          userId: String(e.submitted_by || e.userId || ''),
          userName: e.submitted_by_name || e.userName || '',
          category: e.category || '',
          amount: Number(e.amount || 0),
          date: e.expense_date || e.date || '',
          description: e.description || '',
          status: e.status === 'paid' ? 'approved' : e.status,
          approvedAt: e.approved_at || undefined,
          receiptUrl: e.receipt_path || e.receiptUrl || '',
          vehiclePlate: e.vehicle_plate || '',
          trailerPlate: e.trailer_plate || '',
          iban: e.iban || '',
          ocrData: parseOcrMetadata(e.ocr_metadata) || undefined,
        })));
      }

      if (usersRes.success && usersRes.data) {
        const personnelData: Personnel[] = usersRes.data.map((u: any) => ({
          id: u.id?.toString() || '',
          name: u.full_name || u.name || u.fullName || '',
          iban: u.iban || '',
          bankName: u.bank_name || u.bankName || '',
          role: u.role || '',
          department: u.department_name || u.department || '',
        }));
        setPersonnel(personnelData);
      }

      if (ordersRes.success && ordersRes.data) {
        setPaymentOrders(ordersRes.data);
      }
      setExpenseCategories((categoriesRes.data || categoriesRes || []).map((item: any) => ({ value: item.code, label: item.label })));
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const generateMockIban = () => {
    const randomDigits = () => Math.floor(Math.random() * 10);
    return `TR${randomDigits()}${randomDigits()} ${randomDigits()}${randomDigits()}${randomDigits()}${randomDigits()} ${randomDigits()}${randomDigits()}${randomDigits()}${randomDigits()} ${randomDigits()}${randomDigits()}${randomDigits()}${randomDigits()} ${randomDigits()}${randomDigits()}${randomDigits()}${randomDigits()} ${randomDigits()}${randomDigits()}${randomDigits()}${randomDigits()}`;
  };

  const savePaymentOrders = async (orders: PaymentOrder[]) => {
    setPaymentOrders(orders);
  };

  const createPaymentOrders = async () => {
    if (selectedExpenses.length === 0) {
      alert('Lütfen en az bir masraf seçin');
      return;
    }

    const expensesByPersonnel = new Map<string, Expense[]>();
    expenses
      .filter(e => selectedExpenses.includes(e.id))
      .forEach(expense => {
        const existing = expensesByPersonnel.get(expense.userId) || [];
        expensesByPersonnel.set(expense.userId, [...existing, expense]);
      });

    const newOrders: PaymentOrder[] = [];
    for (const [userId, userExpenses] of expensesByPersonnel) {
      const person = personnel.find(p => p.id === userId);
      const resolvedIban = person?.iban || userExpenses[0]?.iban || '';
      if (!resolvedIban) {
        alert(`${userExpenses[0].userName} için IBAN bilgisi bulunamadı!`);
        continue;
      }

      const order: PaymentOrder = {
        id: Date.now().toString() + userId,
        personnelId: userId,
        personnelName: person.name,
        iban: resolvedIban,
        bankName: person.bankName || 'Bilinmiyor',
        totalAmount: userExpenses.reduce((sum, e) => sum + e.amount, 0),
        expenseIds: userExpenses.map(e => e.id),
        expenseCount: userExpenses.length,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      newOrders.push(order);
    }

    try {
      for (const order of newOrders) {
        await expenseService.createPaymentOrder(order);
      }
      const ordersRes = await expenseService.getPaymentOrders();
      if (ordersRes.success && ordersRes.data) {
        setPaymentOrders(ordersRes.data);
      }
      setSelectedExpenses([]);
      alert(`${newOrders.length} ödeme emri oluşturuldu!`);
    } catch (err) {
      console.error('Error creating payment orders:', err);
      alert('Ödeme emirleri oluşturulurken hata oluştu');
    }
  };

  const exportToBankFile = (orders: PaymentOrder[]) => {
    if (orders.length === 0) {
      alert('Lütfen en az bir ödeme emri seçin');
      return;
    }

    let csv = 'Alıcı Adı,IBAN,Banka,Tutar,Açıklama,Tarih\n';
    
    orders.forEach(order => {
      const row = [
        order.personnelName,
        order.iban.replace(/\s/g, ''),
        order.bankName,
        order.totalAmount.toFixed(2),
        `Masraf Ödemesi - ${order.expenseCount} Adet`,
        new Date().toLocaleDateString('tr-TR'),
      ];
      csv += row.join(',') + '\n';
    });

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `odeme_emirleri_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    const updated = paymentOrders.map(o =>
      orders.some(selected => selected.id === o.id)
        ? { ...o, status: 'sent' as const, sentAt: new Date().toISOString() }
        : o
    );
    savePaymentOrders(updated);
  };

  const markAsPaid = async (orderId: string) => {
    try {
      await expenseService.markPaymentOrderPaid(orderId);
      const ordersRes = await expenseService.getPaymentOrders();
      if (ordersRes.success && ordersRes.data) {
        setPaymentOrders(ordersRes.data);
      }
    } catch (err) {
      console.error('Error updating payment order:', err);
      alert('Ödeme emri güncellenirken hata oluştu');
    }
  };

  const pendingExpenses = expenses.filter(e => 
    !paymentOrders.some(po => po.expenseIds.includes(e.id)) && (selectedDriverId === 'all' || e.userId === selectedDriverId)
  );

  const filteredPaymentOrders = paymentOrders.filter((order) => selectedDriverId === 'all' || order.personnelId === selectedDriverId);

  const getCategoryLabel = (category: string) => {
    return expenseCategories.find((item) => item.value === category)?.label || category || '-';
  };

  const groupedPendingExpenses = pendingExpenses.reduce((acc, expense) => {
    const key = expense.userId || 'unknown';
    const person = personnel.find((item) => item.id === key);
    if (!acc[key]) {
      acc[key] = {
        userId: key,
        userName: expense.userName || '-',
        iban: person?.iban || expense.iban || '',
        bankName: person?.bankName || '',
        totalAmount: 0,
        count: 0,
        items: [] as Expense[],
      };
    }
    acc[key].items.push(expense);
    acc[key].count += 1;
    acc[key].totalAmount += Number(expense.amount || 0);
    return acc;
  }, {} as Record<string, { userId: string; userName: string; iban: string; bankName: string; totalAmount: number; count: number; items: Expense[] }>);

  const bulkMarkSelectedOrdersPaid = async () => {
    if (selectedOrderIds.length === 0) {
      alert('Lütfen en az bir ödeme emri seçin');
      return;
    }

    try {
      await Promise.all(selectedOrderIds.map((orderId) => expenseService.markPaymentOrderPaid(orderId)));
      setSelectedOrderIds([]);
      await loadData();
    } catch (err) {
      console.error('Error bulk marking payment orders paid:', err);
      alert('Toplu odendi islemi sirasinda hata olustu');
    }
  };

  const stats = {
    pendingOrders: paymentOrders.filter(o => o.status === 'pending').length,
    sentOrders: paymentOrders.filter(o => o.status === 'sent').length,
    paidOrders: paymentOrders.filter(o => o.status === 'paid').length,
    totalPending: paymentOrders.filter(o => o.status === 'pending').reduce((sum, o) => sum + o.totalAmount, 0),
    totalPaid: paymentOrders.filter(o => o.status === 'paid').reduce((sum, o) => sum + o.totalAmount, 0),
  };

  const getStatusBadge = (status: PaymentOrder['status']) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Bekliyor</span>;
      case 'sent':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Gönderildi</span>;
      case 'paid':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Ödendi</span>;
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-500">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Ödeme Emirleri</h1>
        <p className="text-sm text-gray-600 mt-1">Onaylanan masraflar için ödeme emirleri oluşturun ve bankaya gönderin</p>
      </div>

      <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900">Surucu filtresi</p>
          <p className="text-xs text-gray-500 mt-1">Onaylanan masraflari ve odeme emirlerini secili surucuye gore filtreleyin.</p>
        </div>
        <select value={selectedDriverId} onChange={(e) => { setSelectedDriverId(e.target.value); setSelectedExpenses([]); setSelectedOrderIds([]); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[240px]">
          <option value="all">Tum suruculer</option>
          {personnel.filter((p) => ['internal-driver', 'driver'].includes(p.role)).map((person) => (
            <option key={person.id} value={person.id}>{person.name}</option>
          ))}
        </select>
      </div>

      {selectedDriverId !== 'all' && filteredPaymentOrders.filter((o) => o.status === 'sent').length > 0 && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-green-900">Secili surucunun gonderilmis odeme emirleri hazir</p>
            <p className="text-xs text-green-800 mt-1">Tek tikla secili surucunun tum gonderilmis odeme emirlerini odendi yapabilirsiniz.</p>
          </div>
          <button onClick={async () => {
            const ids = filteredPaymentOrders.filter((o) => o.status === 'sent').map((o) => o.id);
            setSelectedOrderIds(ids);
            if (ids.length === 0) return;
            try {
              await Promise.all(ids.map((orderId) => expenseService.markPaymentOrderPaid(orderId)));
              setSelectedOrderIds([]);
              await loadData();
            } catch (err) {
              console.error('Error marking all selected driver orders paid:', err);
              alert('Toplu odendi islemi sirasinda hata olustu');
            }
          }} className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-3 text-white font-medium hover:bg-green-700">
            <CheckCircle className="w-4 h-4" />
            Secili Surucunun Tumunu Odendi Yap
          </button>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bekleyen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Gönderildi</p>
              <p className="text-2xl font-bold text-gray-900">{stats.sentOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Ödendi</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paidOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bekleyen Tutar</p>
              <p className="text-xl font-bold text-gray-900">₺{stats.totalPending.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Ödenen Tutar</p>
              <p className="text-xl font-bold text-gray-900">₺{stats.totalPaid.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {pendingExpenses.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">
                Ödeme Emri Bekleyen {pendingExpenses.length} Masraf Var
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Toplam: ₺{pendingExpenses.reduce((sum, e) => sum + e.amount, 0).toFixed(2)}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Seçili sürücü: {selectedDriverId === 'all' ? 'Tümü' : personnel.find((p) => p.id === selectedDriverId)?.name || '-'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedExpenses(pendingExpenses.map(e => e.id))}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Tümünü Seç
              </button>
              <button
                onClick={createPaymentOrders}
                disabled={selectedExpenses.length === 0}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Ödeme Emri Oluştur ({selectedExpenses.length})
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {Object.values(groupedPendingExpenses).map((group) => (
              <div key={`summary-${group.userId}`} className="rounded-xl border border-blue-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{group.userName}</p>
                    <p className="text-xs text-gray-500 mt-1">{group.count} masraf kaydı</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Toplam</p>
                    <p className="font-semibold text-blue-700">₺{group.totalAmount.toFixed(2)}</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-xs text-gray-600">
                  <div>IBAN: <span className="font-medium text-gray-900">{group.iban || 'Tanımlı değil'}</span></div>
                  <div>Banka: <span className="font-medium text-gray-900">{group.bankName || 'Tanımlı değil'}</span></div>
                </div>
              </div>
            ))}
          </div>

          {pendingExpenses.length > 0 && (
            <div className="mt-4 bg-white rounded-lg border border-blue-200 overflow-hidden">
              <table className="w-full min-w-[1200px]">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedExpenses.length === pendingExpenses.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedExpenses(pendingExpenses.map(e => e.id));
                          } else {
                            setSelectedExpenses([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Tarih</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Masraf No</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Personel</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Kategori</th>
                     <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Çekici</th>
                     <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Dorse</th>
                     <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">IBAN</th>
                     <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Açıklama</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-700">Onay Tarihi</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-gray-700">Evrak</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-700">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.values(groupedPendingExpenses).map((group) => (
                    <Fragment key={`group-${group.userId}`}>
                      <tr key={`group-${group.userId}`} className="bg-slate-50 border-t border-slate-200">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={group.items.every((item) => selectedExpenses.includes(item.id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedExpenses((current) => [...new Set([...current, ...group.items.map((item) => item.id)])]);
                              } else {
                                setSelectedExpenses((current) => current.filter((id) => !group.items.some((item) => item.id === id)));
                              }
                            }}
                            className="rounded"
                          />
                        </td>
                         <td colSpan={11} className="px-4 py-3">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{group.userName}</p>
                              <p className="text-xs text-slate-500 mt-1">{group.count} masraf kaydı • Toplam ₺{group.totalAmount.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-1">IBAN: {group.iban || 'Tanımlı değil'} • Banka: {group.bankName || 'Tanımlı değil'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {group.items.map((expense) => (
                        <tr key={expense.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedExpense(expense)}>
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedExpenses.includes(expense.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedExpenses([...selectedExpenses, expense.id]);
                                } else {
                                  setSelectedExpenses(selectedExpenses.filter(id => id !== expense.id));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">{expense.date ? new Date(expense.date).toLocaleDateString('tr-TR') : '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">{expense.expenseNo || '-'}</td>
                          <td className="px-4 py-2 text-sm">{expense.userName}</td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">{getCategoryLabel(expense.category)}</td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            {expense.vehiclePlate ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">
                                <Truck className="w-3 h-3" />
                                {expense.vehiclePlate}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">{expense.trailerPlate || '-'}</td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap font-mono">{expense.iban || '-'}</td>
                          <td className="px-4 py-2 text-sm">{expense.description}</td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">{expense.approvedAt ? new Date(expense.approvedAt).toLocaleDateString('tr-TR') : '-'}</td>
                          <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            {expense.receiptUrl ? (
                              <button
                                type="button"
                                onClick={() => setPreviewReceiptUrl(resolveFileUrl(expense.receiptUrl))}
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                              >
                                <FileText className="w-4 h-4" />
                                Gör
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">Yok</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">₺{expense.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-semibold text-gray-900">Ödeme Emirleri</h2>
          <div className="flex items-center gap-2">
          {filteredPaymentOrders.filter(o => o.status === 'pending').length > 0 && (
            <button
              onClick={() => exportToBankFile(filteredPaymentOrders.filter(o => o.status === 'pending'))}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Download className="w-4 h-4" />
              Bankaya Gonder ({filteredPaymentOrders.filter(o => o.status === 'pending').length})
            </button>
          )}
          {filteredPaymentOrders.filter((o) => o.status === 'sent').length > 0 && (
            <button onClick={bulkMarkSelectedOrdersPaid} disabled={selectedOrderIds.length === 0} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
              <CheckCircle className="w-4 h-4" />
              Toplu Odendi Yap ({selectedOrderIds.length})
            </button>
          )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  <input type="checkbox" checked={filteredPaymentOrders.filter((o) => o.status === 'sent').length > 0 && selectedOrderIds.length === filteredPaymentOrders.filter((o) => o.status === 'sent').length} onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrderIds(filteredPaymentOrders.filter((o) => o.status === 'sent').map((o) => o.id));
                    } else {
                      setSelectedOrderIds([]);
                    }
                  }} className="rounded" />
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Personel</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">IBAN</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Banka</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Masraf Sayısı</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Toplam Tutar</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPaymentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    {order.status === 'sent' ? <input type="checkbox" checked={selectedOrderIds.includes(order.id)} onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrderIds((current) => [...current, order.id]);
                      } else {
                        setSelectedOrderIds((current) => current.filter((id) => id !== order.id));
                      }
                    }} className="rounded" /> : null}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{order.personnelName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono text-gray-900">{order.iban}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{order.bankName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm text-gray-900">{order.expenseCount}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-bold text-gray-900">₺{order.totalAmount.toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {order.status === 'sent' && (
                        <button
                          onClick={() => markAsPaid(order.id)}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Ödendi İşaretle
                        </button>
                      )}
                      {order.status === 'pending' && (
                        <button
                          onClick={() => exportToBankFile([order])}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Gönder
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPaymentOrders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <DollarSign className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500">Henüz ödeme emri oluşturulmamış</p>
          </div>
        )}
      </div>

      {previewReceiptUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex flex-col gap-3 border-b border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Masraf Evrakı</h3>
              <button onClick={() => setPreviewReceiptUrl(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewReceiptUrl.match(/\.pdf($|\?)/i) ? (
                <iframe src={previewReceiptUrl} className="w-full h-[75vh] rounded-lg border border-gray-200" title="Masraf Evrakı" />
              ) : (
                <img src={previewReceiptUrl} alt="Masraf Evrakı" className="w-full max-h-[75vh] object-contain rounded-lg border border-gray-200 bg-white" />
              )}
            </div>
          </div>
        </div>
      )}

      {selectedExpense && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
          <div className="h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 flex flex-col gap-3 border-b border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Masraf Detayı</h3>
                <p className="text-sm text-gray-500 mt-1">{selectedExpense.expenseNo || '-'}</p>
              </div>
              <button onClick={() => setSelectedExpense(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div><p className="text-gray-500">Sürücü</p><p className="font-medium text-gray-900 mt-1">{selectedExpense.userName}</p></div>
                <div><p className="text-gray-500">Kategori</p><p className="font-medium text-gray-900 mt-1">{getCategoryLabel(selectedExpense.category)}</p></div>
                <div><p className="text-gray-500">Tarih</p><p className="font-medium text-gray-900 mt-1">{selectedExpense.date ? new Date(selectedExpense.date).toLocaleDateString('tr-TR') : '-'}</p></div>
                <div><p className="text-gray-500">Onay Tarihi</p><p className="font-medium text-gray-900 mt-1">{selectedExpense.approvedAt ? new Date(selectedExpense.approvedAt).toLocaleDateString('tr-TR') : '-'}</p></div>
                <div><p className="text-gray-500">Çekici</p><p className="font-medium text-gray-900 mt-1">{selectedExpense.vehiclePlate || '-'}</p></div>
                <div><p className="text-gray-500">Dorse</p><p className="font-medium text-gray-900 mt-1">{selectedExpense.trailerPlate || '-'}</p></div>
                <div><p className="text-gray-500">IBAN</p><p className="font-medium text-gray-900 mt-1 break-all font-mono">{selectedExpense.iban || '-'}</p></div>
                <div><p className="text-gray-500">Tutar</p><p className="font-medium text-gray-900 mt-1">₺{selectedExpense.amount.toFixed(2)}</p></div>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Açıklama</p>
                <div className="mt-1 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-gray-900">{selectedExpense.description || '-'}</div>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Evrak</p>
                <div className="mt-2">
                  {selectedExpense.receiptUrl ? (
                    <button type="button" onClick={() => setPreviewReceiptUrl(resolveFileUrl(selectedExpense.receiptUrl))} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
                      <FileText className="w-4 h-4" /> Evrakı Gör
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">Yüklü evrak yok.</p>
                  )}
                </div>
              </div>
              {selectedExpense.ocrData && (
                <>
                  <div>
                    <p className="text-gray-500 text-sm">OCR Özeti</p>
                    <div className="mt-1 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-gray-900 space-y-1">
                      <div>İşyeri: {selectedExpense.ocrData.merchant || '-'}</div>
                      <div>Fiş Tarihi: {selectedExpense.ocrData.date || '-'}</div>
                      <div>VKN: {selectedExpense.ocrData.taxNumber || '-'}</div>
                      <div>Kategori Önerisi: {getCategoryLabel(selectedExpense.ocrData.suggestedCategory || '')}</div>
                      <div>Ara Toplam: {selectedExpense.ocrData.summary?.subtotal != null ? `₺${Number(selectedExpense.ocrData.summary.subtotal).toFixed(2)}` : '-'}</div>
                      <div>KDV: {selectedExpense.ocrData.summary?.vat != null ? `₺${Number(selectedExpense.ocrData.summary.vat).toFixed(2)}` : '-'}</div>
                      <div>Genel Toplam: {selectedExpense.ocrData.summary?.grandTotal != null ? `₺${Number(selectedExpense.ocrData.summary.grandTotal).toFixed(2)}` : '-'}</div>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Satır Satır Fiş Kırılımı</p>
                    <div className="mt-1 rounded-lg bg-slate-50 border border-slate-200 p-3 max-h-52 overflow-y-auto space-y-2 text-sm text-gray-900">
                      {selectedExpense.ocrData.items?.length ? selectedExpense.ocrData.items.map((item, index) => (
                        <div key={`${item.raw}-${index}`} className="flex items-start justify-between gap-3 border-b border-slate-200 pb-2 last:border-b-0 last:pb-0">
                          <span>{item.description}</span>
                          <span className="font-medium whitespace-nowrap">₺{Number(item.amount).toFixed(2)}</span>
                        </div>
                      )) : <div className="text-gray-500">Kırılım bulunamadı.</div>}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Tam OCR İçeriği</p>
                    <pre className="mt-1 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-gray-700 whitespace-pre-wrap break-words max-h-52 overflow-y-auto">{selectedExpense.ocrData.rawText || '-'}</pre>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
